/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import { useQuery } from '@apollo/client';
import { ConfirmModal } from '@coursera-web/cds-labs';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { get as getUser } from 'js/lib/user';

import { Button, Card, Grid, InlineNotification, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import PartnerHelpLink from 'bundles/authoring/common/components/PartnerHelpLink';
import useZoomDev from 'bundles/third-party-auth/components/AccountLink/hooks/useZoomDev';
import delay from 'bundles/third-party-auth/components/AccountLink/utils/delay';
import { ZoomAuthEmailQuery, getZoomOAuth2Link } from 'bundles/third-party-auth/helpers/zoom';
import { unlink as unlinkZoom } from 'bundles/third-party-auth/utils/ZoomAuthAPIUtils';

import _t from 'i18n!nls/third-party-auth';

const styles = {
  card: css`
    &.cds-Card-base .cds-Card-innerCard {
      padding: var(--cds-spacing-200);
    }
  `,
  row: css`
    column-gap: var(--cds-spacing-200);
    align-items: flex-start;
    flex-wrap: wrap;

    ${breakpoints.down('xs')} {
      column-gap: var(--cds-spacing-100);
    }
  `,
  textColumn: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  ctaColumn: css`
    padding-top: var(--cds-spacing-50);
  `,
  liveRegion: css`
    &:not(:empty) {
      margin-top: var(--cds-spacing-200);
    }
  `,
};

type ZoomAuthEmailQueryData = {
  ZoomAuthV1Resource?: {
    elements?: Array<{ id: string; email?: string }>;
  };
};

const AccountLinkZoom = () => {
  const isDev = useZoomDev();
  const { data, refetch } = useQuery<ZoomAuthEmailQueryData>(ZoomAuthEmailQuery, {
    variables: { isDev },
  });
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [unlinkSuccess, setUnlinkSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const track = useTracker();
  const returnRef = useRef<HTMLDivElement | null>(null);
  const successRef = useRef<HTMLDivElement>(null);

  if (!getUser().is_staff) {
    return null;
  }

  const linkedAccount = data?.ZoomAuthV1Resource?.elements?.[0];
  const linkedEmail = linkedAccount?.email;

  const handleUnlinkClick = () => {
    track('click_button', {
      button: { name: 'account_setting_unlink_zoom_btn' },
    });
    returnRef.current = null;
    setError(null);
    setShowUnlinkModal(true);
  };

  const handleLinkClick = () => {
    track('click_button', {
      button: { name: 'account_setting_link_zoom_btn' },
    });
  };

  const closeUnlinkModal = () => {
    setShowUnlinkModal(false);
  };

  const handleUnlink = () => {
    unlinkZoom(isDev)
      .then(() => delay(1500))
      .then(() => refetch())
      .then(() => {
        returnRef.current = successRef.current;
        setShowUnlinkModal(false);
        setError(null);
        setUnlinkSuccess(true);
      })
      .catch(() => {
        setShowUnlinkModal(false);
        setError(_t("Uh oh! We weren't able to unlink your Zoom account. Please try again later."));
      });
  };

  return (
    <>
      <Card css={styles.card} interactive={false}>
        <Grid container css={styles.row}>
          <Grid item xs css={styles.textColumn}>
            <Typography2 variant="subtitleLarge" component="h3">
              {_t('Zoom')}
            </Typography2>
            {linkedAccount ? (
              <Typography2 variant="bodySecondary" component="p" color="supportText">
                {linkedEmail ? (
                  <FormattedHTMLMessage
                    message={_t(
                      'You can add meetings as live events in your courses. Your linked Zoom email is <strong>#{email}</strong>.',
                      { email: linkedEmail }
                    )}
                  />
                ) : (
                  _t('You can add meetings as live events in your courses.')
                )}
              </Typography2>
            ) : (
              <Typography2 variant="bodySecondary" component="p" color="supportText">
                {_t(
                  `Linking with Zoom will give you the ability to add meetings as live events in your courses. You'll need to be part of a Zoom Pro, Business, or Enterprise account (i.e. one managed by your institution).`
                )}{' '}
                <PartnerHelpLink articleId="360046759851" />
              </Typography2>
            )}
          </Grid>
          <Grid item xs={12} sm="auto" css={styles.ctaColumn}>
            {linkedAccount ? (
              <Button variant="secondary" size="small" onClick={handleUnlinkClick} data-e2e="unlink-with-zoom">
                {_t('Unlink')}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="small"
                component="a"
                href={getZoomOAuth2Link(isDev)}
                onClick={handleLinkClick}
                data-e2e="link-with-zoom"
              >
                {_t('Link with Zoom')}
              </Button>
            )}
          </Grid>
          <Grid item xs={12} css={styles.liveRegion}>
            {/* tabIndex={-1} + returnFocusRef: the trigger button swaps on unlink, so the
               dialog's default focus restore announces the new button and drowns out the
               polite live region. We redirect return focus here instead. */}
            <div ref={successRef} tabIndex={-1} role="status" aria-live="polite" aria-atomic="true">
              {unlinkSuccess && (
                <InlineNotification severity="success">
                  {_t('Successfully unlinked your Zoom account.')}
                </InlineNotification>
              )}
            </div>
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {error && <InlineNotification severity="error">{error}</InlineNotification>}
            </div>
          </Grid>
        </Grid>
      </Card>
      <ConfirmModal
        open={showUnlinkModal}
        headerLabel={_t('Are you sure you want to unlink from Zoom?')}
        acceptLabel={_t('Unlink')}
        onAccept={handleUnlink}
        onCancel={closeUnlinkModal}
        returnFocusRef={returnRef}
      >
        {_t('You can always link again later.')}
      </ConfirmModal>
    </>
  );
};

export default AccountLinkZoom;
